import { useMutation, useQuery, useQueryClient } from 'react-query';

import { del, get, post } from '../../http';
import { FriendDraftType, FriendResponseType, FriendType, FriendsResponseType } from './types';

type EnhancedFriendType = FriendType & {
  email: string | null;
  name: string | null;
};

const KEY = ['profile', 'friends'];

const enhanceFriendData = (friend: FriendType): EnhancedFriendType => ({
  ...friend,
  email: friend.mail,
  name: friend.firstName ? `${friend.firstName} ${friend.lastName}` : null,
});

export const useFriends = () =>
  useQuery(KEY, async () => {
    const friends = await getFriends();

    if (!friends) return [];

    return friends.map(({ friend }) => enhanceFriendData(friend));
  });

export const useDeleteFriend = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteFriend, {
    onSuccess: (_, friendId) => {
      queryClient.setQueryData<EnhancedFriendType[] | undefined>(KEY, friends =>
        friends?.filter(friend => friend.id !== friendId)
      );
    },
  });
};

export const useAddFriend = () => {
  const queryClient = useQueryClient();

  return useMutation(addFriend, {
    onSuccess: response => {
      queryClient.setQueryData<EnhancedFriendType[] | undefined>(KEY, friends => {
        if (!response?.friend) return friends;

        const newFriend = enhanceFriendData(response.friend);

        if (!friends) return [newFriend];

        return [...friends, newFriend];
      });
    },
  });
};

const getFriends = () => get<FriendsResponseType>('Userfriends/Me');

const deleteFriend = (friendId: FriendType['id']) => del(`Userfriends/${friendId}`);

const addFriend = (friend: FriendDraftType) => post<FriendResponseType>('Userfriends', friend);
